import cx from "classnames";
import { FC, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";

import EMITTER, { Events } from "./../../emitter";
import usePopup from "./../../hooks/usePopup";
import useShallowSelector from "./../../hooks/useShalowSelector";
import { LoginPopupState, PopupTypes } from "./../../store/types/Popup";
import {
    changeAuthPopUpRender,
    changeCurrentLoginPopupState,
    changeCurrentPopup,
} from "./../../store/ui/actionCreators";
import { selectUIAuthPopUpRender, selectUIPopup } from "./../../store/ui/selectors";
import Content from "./Content";

import styles from "./styles.module.scss";

const Popup: FC = () => {
    const [callback, setCallback] = useState<null | Function>(null);
    const [popupType, setPopupType] = useState<null | PopupTypes>(null);
    const [initialText, setInitialText] = useState<undefined | string>();
    const current = useShallowSelector(selectUIPopup);
    const dispatch = useDispatch();
    const { onClose } = usePopup();
    const popupRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);
    const authProps = useShallowSelector(selectUIAuthPopUpRender);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        const currentEvent = EMITTER.addListener(
            Events.POPUP_OPEN,
            (type: PopupTypes, upcomingCallBack: () => void, initialText?: string) => {
                upcomingCallBack && setCallback(() => upcomingCallBack);
                dispatch(changeCurrentPopup(type));
                if (initialText) {
                    setInitialText(initialText);
                }
            },
        );

        return () => currentEvent.remove();
    }, [dispatch]);

    const { onOpen: onPopupOpen } = usePopup();

    const getOpenPopupHandler = (popupType: PopupTypes, newCurrentLoginPopupState: LoginPopupState | null) => {
        onPopupOpen(popupType);
        dispatch(changeAuthPopUpRender({ authPopUpRender: true }));
        dispatch(changeCurrentLoginPopupState(newCurrentLoginPopupState));
    };
    useEffect(() => {
        getOpenPopupHandler(PopupTypes.LOGIN_SIGNUP, LoginPopupState.LOGIN);
        setTimeout(() => {
            onClose();
            setPopupType(null);
            dispatch(changeAuthPopUpRender({ authPopUpRender: false }));
        }, 1000);
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (current !== null && popupType !== current) setPopupType(current);
    }, [current, popupType, setPopupType]);

    if (popupType === null) return null;

    const handleonClose = () => {
        if (isMounted) onClose(popupType);
    };

    const doNothing = () => [];
    const isDoNothing = popupType === PopupTypes.HTML_EDITOR || popupType === PopupTypes.FONTS;

    return ReactDOM.createPortal(
        <div
            className={cx(styles.overlay, {
                [styles.active]: current !== null,
                [styles.inactive]: current === null,
                [styles.doNothing]: isDoNothing,
                [styles.authPopUpRender]: authProps,
            })}
            onMouseDown={isDoNothing ? doNothing : handleonClose}
            onAnimationEnd={() => {
                if (current === null && popupType !== null) {
                    if (popupType === PopupTypes.LOGIN_SIGNUP) dispatch(changeCurrentLoginPopupState(null));
                    setPopupType(null);
                }
            }}
        >
            <div className={styles.wrapper} ref={popupRef} onMouseDown={e => e.stopPropagation()}>
                <Content callback={callback} current={popupType} onClose={onClose} initialText={initialText} />
            </div>
        </div>,
        document.getElementById("popup-portal") as HTMLElement,
    );
};

export default Popup;
